import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { callFunction } from '../firebase/functions';
import db, { doc, updateDoc, serverTimestamp } from '../firebase/firestore';
import { currentPortal } from '../utils/helpCentre';

// One Help Centre conversation per browser tab, shared by the slide-over
// panel (HelpButton in the Topbar) and the full Help page, so it carries on
// as staff move between pages. Mounted by PageShell. The server saves every
// question (helpQuestions); the conversation itself is only held here.
const HelpCentreContext = createContext(null);

const newConversationId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

export function HelpCentreProvider({ children }) {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('ask');
  const [articleId, setArticleId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(newConversationId);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const ask = useCallback(async (question) => {
    const q = question.trim();
    if (!q || sending) return false;
    setError('');
    const history = messages.map(({ role, text }) => ({ role, text }));
    setMessages((prev) => [...prev, { role: 'user', text: q }]);
    setSending(true);
    try {
      const { data } = await callFunction('askHelp', { timeout: 85000 })({
        question: q,
        history,
        route: pathname,
        portal: currentPortal(pathname, user?.uid),
        language: i18n.language?.startsWith('es') ? 'es' : 'en',
        conversationId,
      });
      setMessages((prev) => [...prev, {
        role: 'assistant',
        text: data.answer,
        questionId: data.questionId,
        articles: data.articles || [],
        covered: data.covered,
        offTopic: data.offTopic,
        rating: null,
        escalated: false,
      }]);
      return true;
    } catch (err) {
      setMessages((prev) => prev.slice(0, -1));
      setError(err.message || 'Something went wrong. Please try again.');
      return false;
    } finally {
      setSending(false);
    }
  }, [messages, sending, pathname, user?.uid, i18n.language, conversationId]);

  const updateAnswer = (questionId, patch) => {
    setMessages((prev) => prev.map((m) => (m.questionId === questionId ? { ...m, ...patch } : m)));
  };

  const rate = useCallback(async (questionId, rating) => {
    updateAnswer(questionId, { rating });
    try {
      await updateDoc(doc(db, 'helpQuestions', questionId), { rating, ratedAt: serverTimestamp() });
    } catch {
      updateAnswer(questionId, { rating: null });
    }
  }, []);

  const escalate = useCallback(async (questionId, note) => {
    await updateDoc(doc(db, 'helpQuestions', questionId), {
      escalated: true,
      escalatedAt: serverTimestamp(),
      escalationNote: (note || '').trim().slice(0, 1000),
    });
    updateAnswer(questionId, { escalated: true });
  }, []);

  const resetChat = useCallback(() => {
    setMessages([]);
    setError('');
    setConversationId(newConversationId());
  }, []);

  const openArticle = useCallback((id) => {
    setArticleId(id);
    setTab('browse');
  }, []);

  const value = useMemo(() => ({
    open, setOpen, tab, setTab, articleId, setArticleId, openArticle,
    messages, sending, error, ask, rate, escalate, resetChat,
  }), [open, tab, articleId, openArticle, messages, sending, error, ask, rate, escalate, resetChat]);

  return <HelpCentreContext.Provider value={value}>{children}</HelpCentreContext.Provider>;
}

export function useHelpCentre() {
  return useContext(HelpCentreContext);
}
